/**
 * @file backup-destination.ts
 * @description «Где хранить копии» (phase19.26 шаг 7; шаг 7.1): реестр типов
 * назначения, черновик формы, смена типа и сборка PATCH.
 *
 * Типы — данными (`DESTINATION_META`): подпись, подсказка, какие поля нужны и
 * какие учётные данные. Новый тип = запись, а не ветка в разметке.
 *
 * Секреты сервер не отдаёт (только `hasCredentials` и `credentialsLogin` под маской),
 * поэтому поля секретов в форме пустые: не трогали — `destinationCredentials`
 * не отправляем («оставить как есть»); заполнили — отправляем целиком;
 * LOCAL — `null` (стереть). Сменили тип — старые данные не подходят, нужны
 * новые.
 *
 * Шаг 7.1 (Р1): папку локальных копий задаёт команда установки, поэтому для
 * LOCAL путь в форме не редактируется и в PATCH не уходит. Р4: внешние типы
 * доступны только при `externalDestinationsEnabled`.
 */

import type {
  BackupConfigDto,
  BackupDestinationCredentials,
  BackupDestinationType,
  UpdateBackupConfigPayload,
} from "../../../lib/api-backups";

/**
 * Какие учётные данные просит тип. `userOptionalPassword` — SMB: логин
 * обязателен, пароль — нет (гостевая общая папка), как в API.
 */
export type CredentialsKind =
  | "none"
  | "userPassword"
  | "userOptionalPassword"
  | "userPasswordOrKey"
  | "s3";

export interface DestinationMeta {
  label: string;
  hint: string;
  /** null — адрес не нужен (LOCAL). */
  url: { label: string; placeholder: string } | null;
  path: { label: string; placeholder: string };
  credentials: CredentialsKind;
}

export const DESTINATION_TYPES: BackupDestinationType[] = ["LOCAL", "SMB", "SFTP", "S3", "WEBDAV"];

export const DESTINATION_META: Record<BackupDestinationType, DestinationMeta> = {
  LOCAL: {
    label: "Локально на сервере",
    hint: "Копии лежат на отдельном разделе диска сервера приложения.",
    url: null,
    path: { label: "Папка на сервере", placeholder: "/srv/chaiberi/backups" },
    credentials: "none",
  },
  SMB: {
    label: "Сетевая папка SMB",
    hint: "Общая папка на NAS или компьютере в локальной сети (Windows, Synology и т. п.).",
    url: { label: "Адрес в сети", placeholder: "192.168.1.10 или nas.local" },
    path: { label: "Общая папка и путь", placeholder: "backups/chaiberi" },
    credentials: "userOptionalPassword",
  },
  SFTP: {
    label: "SFTP (другой сервер)",
    hint: "Другой сервер с доступом по SSH — копии идут по зашифрованному каналу.",
    url: { label: "Хост и порт", placeholder: "backup.example.ru:22" },
    path: { label: "Папка на сервере", placeholder: "/home/backup/chaiberi" },
    credentials: "userPasswordOrKey",
  },
  S3: {
    label: "Хранилище S3 по HTTPS",
    hint: "Облачное объектное хранилище (Yandex Object Storage, VK Cloud, Selectel и др.).",
    url: { label: "Адрес хранилища (endpoint)", placeholder: "https://storage.yandexcloud.net" },
    path: { label: "Бакет и папка", placeholder: "chaiberi-backups/prod" },
    credentials: "s3",
  },
  WEBDAV: {
    label: "WebDAV по HTTPS",
    hint: "Облачный диск с WebDAV (Яндекс Диск, Nextcloud и др.).",
    url: { label: "Адрес WebDAV", placeholder: "https://webdav.yandex.ru" },
    path: { label: "Папка", placeholder: "/Backups/chaiberi" },
    credentials: "userPassword",
  },
};

/** Пояснение, пока внешние хранилища закрыты (шаг 7.1, Р4). */
export const EXTERNAL_DISABLED_TEXT =
  "Внешнее хранилище появится после добавления шифрования копий — сейчас копии хранятся только на сервере приложения.";

/** Пункты списка «Хранилище»: внешние видны, но недоступны, пока флаг выключен. */
export function destinationTypeOptions(
  externalEnabled: boolean,
): Array<{ value: BackupDestinationType; label: string; disabled: boolean }> {
  return DESTINATION_TYPES.map((t) => ({
    value: t,
    label: DESTINATION_META[t].label,
    disabled: t !== "LOCAL" && !externalEnabled,
  }));
}

export interface DestinationDraft {
  type: BackupDestinationType;
  url: string;
  path: string;
  keepLocalCopy: boolean;
  /** SFTP: вход по паролю или по ключу. */
  sftpAuth: "password" | "key";
  user: string;
  password: string;
  privateKey: string;
  accessKey: string;
  secretKey: string;
  region: string;
}

/** Пустые адрес, путь и учётные данные — общая часть сброса. */
const BLANK_ACCESS: Omit<DestinationDraft, "type" | "keepLocalCopy"> = {
  url: "",
  path: "",
  sftpAuth: "password",
  user: "",
  password: "",
  privateKey: "",
  accessKey: "",
  secretKey: "",
  region: "",
};

export function destinationDraftFrom(cfg: BackupConfigDto): DestinationDraft {
  const type = cfg.destinationType ?? "LOCAL";
  return {
    // Секреты и логин сервер не отдаёт — поля пустые, «оставить как есть».
    ...BLANK_ACCESS,
    type,
    url: cfg.destinationUrl ?? "",
    // Путь LOCAL в форме не участвует (его задаёт установка) — в черновик не берём.
    path: type === "LOCAL" ? "" : (cfg.destinationPath ?? ""),
    keepLocalCopy: cfg.keepLocalCopy ?? true,
  };
}

/**
 * Смена типа в черновике: адрес, путь и учётные данные очищаются (данные одного
 * типа не подходят другому). Возврат к сохранённому типу (`saved` — черновик
 * из серверных данных) восстанавливает сохранённые адрес и путь; секреты
 * остаются пустыми — «как есть на сервере».
 */
export function switchDestinationType(
  draft: DestinationDraft,
  next: BackupDestinationType,
  saved: DestinationDraft,
): DestinationDraft {
  if (next === draft.type) return draft;
  const cleared: DestinationDraft = { ...draft, ...BLANK_ACCESS, type: next };
  return next === saved.type ? { ...cleared, url: saved.url, path: saved.path } : cleared;
}

/** Собранные учётные данные или текст ошибки; null — поля не трогали. */
function buildCredentials(
  d: DestinationDraft,
): { value: BackupDestinationCredentials } | { error: string } | null {
  const t = (v: string) => v.trim();
  const kind = DESTINATION_META[d.type].credentials;
  if (kind === "s3") {
    if (!t(d.accessKey) && !t(d.secretKey) && !t(d.region)) return null;
    if (!t(d.accessKey) || !t(d.secretKey)) return { error: "Укажите ключ доступа и секретный ключ." };
    return {
      value: { accessKey: t(d.accessKey), secretKey: t(d.secretKey), ...(t(d.region) ? { region: t(d.region) } : {}) },
    };
  }
  const byKey = kind === "userPasswordOrKey" && d.sftpAuth === "key";
  const secret = byKey ? d.privateKey.trim() : d.password;
  if (!t(d.user) && !secret) return null;
  if (kind === "userOptionalPassword") {
    // SMB: для гостевой папки достаточно логина (например, guest), пароль — по желанию.
    if (!t(d.user)) return { error: "Укажите логин (для гостевой папки — guest)." };
    return { value: secret ? { user: t(d.user), password: secret } : { user: t(d.user) } };
  }
  if (!t(d.user) || !secret) {
    return { error: byKey ? "Укажите логин и закрытый ключ." : "Укажите логин и пароль." };
  }
  return { value: byKey ? { user: t(d.user), privateKey: secret } : { user: t(d.user), password: secret } };
}

/**
 * Тело PATCH «Где хранить копии». `saved` — что сейчас на сервере: при том же
 * типе и сохранённых данных пустые поля секретов означают «оставить как есть».
 */
export function buildDestinationPayload(
  d: DestinationDraft,
  saved: Pick<BackupConfigDto, "destinationType" | "hasCredentials">,
): { ok: true; payload: UpdateBackupConfigPayload } | { ok: false; error: string } {
  if (d.type === "LOCAL") {
    return {
      ok: true,
      // Локальная копия — единственная, её нельзя «не оставлять»; данные внешнего
      // хранилища стираем. Папку задаёт установка — путь не отправляем (Р1).
      payload: {
        destinationType: "LOCAL",
        destinationUrl: null,
        keepLocalCopy: true,
        destinationCredentials: null,
      },
    };
  }
  const meta = DESTINATION_META[d.type];
  if (meta.url && !d.url.trim()) return { ok: false, error: `Заполните поле «${meta.url.label}».` };
  if (!d.path.trim()) return { ok: false, error: `Заполните поле «${meta.path.label}».` };
  const creds = buildCredentials(d);
  if (creds && "error" in creds) return { ok: false, error: creds.error };
  const typeChanged = d.type !== saved.destinationType;
  if (!creds && (typeChanged || !saved.hasCredentials)) {
    return { ok: false, error: "Укажите учётные данные для подключения к хранилищу." };
  }
  return {
    ok: true,
    payload: {
      destinationType: d.type,
      destinationUrl: d.url.trim(),
      destinationPath: d.path.trim(),
      keepLocalCopy: d.keepLocalCopy,
      ...(creds ? { destinationCredentials: creds.value } : {}),
    },
  };
}

/** Имя поля из ошибки сервера → подпись поля для выбранного типа. */
export function destinationFieldLabel(field: string, type: BackupDestinationType): string {
  const meta = DESTINATION_META[type];
  const labels: Record<string, string | undefined> = {
    destinationType: "Хранилище",
    destinationUrl: meta.url?.label,
    destinationPath: meta.path.label,
    destinationCredentials: "Учётные данные",
    keepLocalCopy: "Локальная копия",
  };
  return labels[field] ?? "";
}
